import React, { useCallback, useEffect, useState } from 'react';
import { CalendarCheck, CheckCircle2, Plus, Trash2, Wand2 } from 'lucide-react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';

const VIDE = {
  client_nom: '', prestation: '', commune: '', surface_ha: '', duree_jours: '1', date_souhaitee: '', date_limite: '',
  responsable: '', nb_personnes: '1', materiels_requis: [], priorite: 'normale', notes: '',
};
const num = (v) => parseFloat(String(v ?? '').replace(',', '.')) || 0;
const dateFr = (d) => (d ? new Date(`${d}T12:00:00`).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }) : '');
const nomPersonne = (m) => m.name || m.nom || m.prenom || m.email;
const PRIO = { urgente: 'bg-red-100 text-red-800', normale: 'bg-stone-100 text-stone-700', flexible: 'bg-green-100 text-green-800' };

function FormChantier({ form, setForm, materiels }) {
  const [filtre, setFiltre] = useState('');
  const visibles = materiels.filter(m => form.materiels_requis.includes(m.id) || (m.nom || '').toLowerCase().includes(filtre.toLowerCase())).slice(0, 60);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const toggleMat = (id) => setForm({
    ...form,
    materiels_requis: form.materiels_requis.includes(id) ? form.materiels_requis.filter(x => x !== id) : [...form.materiels_requis, id],
  });
  const champ = (k, label, props = {}) => (
    <div className="space-y-1">
      <Label>{label}</Label>
      <Input value={form[k]} onChange={set(k)} data-testid={`car-${k}`} {...props} />
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-3">
      {champ('client_nom', 'Client *')}
      {champ('prestation', 'Prestation *', { placeholder: 'Arrachage, prétaillage, broyage…' })}
      {champ('commune', 'Commune')}
      {champ('surface_ha', 'Surface (ha)', { inputMode: 'decimal' })}
      {champ('duree_jours', 'Durée (jours)', { inputMode: 'decimal' })}
      {champ('nb_personnes', 'Personnes nécessaires', { inputMode: 'numeric' })}
      {champ('date_souhaitee', 'À partir du', { type: 'date' })}
      {champ('date_limite', 'Au plus tard le', { type: 'date' })}
      {champ('responsable', 'Responsable', { placeholder: 'Fred, Laurent…' })}
      <div className="space-y-1">
        <Label>Priorité</Label>
        <select value={form.priorite} onChange={set('priorite')} className="w-full border rounded-md h-10 px-2 bg-white" data-testid="car-priorite">
          <option value="urgente">Urgente</option><option value="normale">Normale</option><option value="flexible">Flexible</option>
        </select>
      </div>
      <div className="col-span-2 space-y-1">
        <Label>Matériel nécessaire</Label>
        <Input placeholder="Chercher un matériel (tracteur, broyeur…)" value={filtre} onChange={e => setFiltre(e.target.value)} data-testid="car-mat-recherche" />
        <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
          {visibles.map(m => (
            <button type="button" key={m.id} onClick={() => toggleMat(m.id)} data-testid={`car-mat-${m.id}`}
              className={`px-2 py-1 rounded-full text-xs border transition-colors ${form.materiels_requis.includes(m.id) ? 'bg-green-700 text-white border-green-700' : 'bg-white text-stone-700 hover:bg-stone-100'}`}>
              {m.nom}
            </button>
          ))}
        </div>
      </div>
      <div className="col-span-2 space-y-1">
        <Label>Notes</Label>
        <Textarea value={form.notes} onChange={set('notes')} rows={2} data-testid="car-notes" />
      </div>
    </div>
  );
}

function Proposition({ prop, setProp, equipe, materiels }) {
  const toggle = (id) => setProp({ ...prop, employes: prop.employes.includes(id) ? prop.employes.filter(x => x !== id) : [...prop.employes, id] });
  const noms = (ids, liste, f) => ids.map(id => f(liste.find(x => x.id === id) || { nom: id })).join(', ') || 'aucun';
  return (
    <div className="space-y-3 text-sm" data-testid="car-proposition">
      <div><b>Jours :</b> {prop.jours.map(dateFr).join(', ')}
        {prop.depasse_limite && <span className="ml-2 text-red-700 font-medium">dépasse la date limite</span>}</div>
      {prop.employes.length !== prop.besoin && <div className="text-amber-700">Il faut {prop.besoin} personne(s) pour ce chantier, {prop.employes.length} sélectionnée(s).</div>}
      <div><b>Matériel :</b> {noms(prop.materiels, materiels, m => m.nom)}</div>
      <div>
        <b>Équipe</b> (cliquez pour changer) :
        <div className="flex flex-wrap gap-2 mt-1">
          {equipe.map(m => (
            <button type="button" key={m.id} onClick={() => toggle(m.id)} data-testid={`car-emp-${m.id}`}
              className={`px-2 py-1 rounded-full text-xs border ${prop.employes.includes(m.id) ? 'bg-green-700 text-white border-green-700' : 'bg-white hover:bg-stone-100'}`}>
              {nomPersonne(m)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function LigneChantier({ c, materiels, equipe, onPlanifier, onTerminer, onSupprimer }) {
  const mats = (c.materiels_requis || []).map(id => materiels.find(m => m.id === id)?.nom).filter(Boolean);
  return (
    <div className="flex items-start justify-between gap-4 p-4 border-b last:border-0" data-testid={`car-ligne-${c.id}`}>
      <div className="space-y-1">
        <div className="font-semibold">{c.prestation} — {c.client_nom}
          <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${PRIO[c.priorite] || PRIO.normale}`}>{c.priorite}</span></div>
        <div className="text-sm text-stone-600">
          {[c.commune, c.surface_ha && `${c.surface_ha} ha`, `${c.duree_jours} j`, `${c.nb_personnes} pers.`, c.responsable && `resp. ${c.responsable}`].filter(Boolean).join(' · ')}
          {c.date_limite && <> · avant le {dateFr(c.date_limite)}</>}
        </div>
        {mats.length > 0 && <div className="text-xs text-stone-500">Matériel : {mats.join(', ')}</div>}
        {c.statut === 'planifie' && (
          <div className="text-xs text-green-800">Planifié : {(c.jours_planifies || []).map(dateFr).join(', ')} · {(c.employes || []).map(id => nomPersonne(equipe.find(m => m.id === id) || { name: '?' })).join(', ')}</div>
        )}
      </div>
      <div className="flex gap-2 shrink-0">
        <Button size="sm" onClick={() => onPlanifier(c)} className="bg-green-700 hover:bg-green-800 text-white" data-testid={`car-planifier-${c.id}`}>
          <Wand2 className="w-4 h-4 mr-1" /> {c.statut === 'planifie' ? 'Replanifier' : 'Proposer un créneau'}
        </Button>
        <Button size="sm" variant="outline" onClick={() => onTerminer(c)} title="Terminé" data-testid={`car-terminer-${c.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
        <Button size="sm" variant="outline" onClick={() => onSupprimer(c)} title="Supprimer" data-testid={`car-supprimer-${c.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
      </div>
    </div>
  );
}

export function ChantiersARealiser({ equipe, materiels, onPlanifie }) {
  const [chantiers, setChantiers] = useState([]);
  const [form, setForm] = useState(VIDE);
  const [ouvert, setOuvert] = useState(false);
  const [cible, setCible] = useState(null);
  const [prop, setProp] = useState(null);
  const [occupe, setOccupe] = useState(false);

  const charger = useCallback(async () => {
    const r = await api.get('/planning-eta/a-realiser');
    setChantiers(r.data.chantiers || []);
  }, []);
  useEffect(() => { charger().catch(() => toast.error('Chargement des chantiers impossible')); }, [charger]);

  const creer = async () => {
    if (!form.client_nom || !form.prestation) { toast.error('Client et prestation obligatoires'); return; }
    setOccupe(true);
    try {
      await api.post('/planning-eta/a-realiser', {
        ...form, surface_ha: form.surface_ha ? num(form.surface_ha) : null, duree_jours: num(form.duree_jours) || 1,
        nb_personnes: parseInt(form.nb_personnes, 10) || 1, date_souhaitee: form.date_souhaitee || null, date_limite: form.date_limite || null,
      });
      toast.success('Chantier ajouté');
      setOuvert(false); setForm(VIDE); charger();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setOccupe(false); }
  };

  const proposer = async (c) => {
    setCible(c); setProp(null); setOccupe(true);
    try {
      const r = await api.post(`/planning-eta/a-realiser/${c.id}/proposition`, { employes_candidats: equipe.map(m => m.id) });
      if (!r.data.trouve) { toast.error(r.data.message); setCible(null); return; }
      setProp({ ...r.data, besoin: c.nb_personnes || 1 });
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); setCible(null); } finally { setOccupe(false); }
  };

  const valider = async () => {
    setOccupe(true);
    try {
      const r = await api.post(`/planning-eta/a-realiser/${cible.id}/planifier`, { jours: prop.jours, employes: prop.employes, materiels: prop.materiels });
      toast.success(`Planifié : ${r.data.taches} créneaux ajoutés au planning`);
      setCible(null); setProp(null); charger(); onPlanifie?.();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); } finally { setOccupe(false); }
  };

  const action = async (c, quoi) => {
    if (quoi === 'supprimer' && !window.confirm(`Supprimer « ${c.prestation} — ${c.client_nom} » et ses créneaux ?`)) return;
    await (quoi === 'supprimer' ? api.delete(`/planning-eta/a-realiser/${c.id}`) : api.post(`/planning-eta/a-realiser/${c.id}/terminer`));
    toast.success(quoi === 'supprimer' ? 'Supprimé' : 'Marqué terminé');
    charger(); onPlanifie?.();
  };

  const aPlanifier = chantiers.filter(c => c.statut === 'a_planifier').length;
  return (
    <Card data-testid="chantiers-a-realiser">
      <CardContent className="p-0">
        <div className="flex items-center justify-between p-4 border-b">
          <div>
            <div className="font-semibold flex items-center gap-2"><CalendarCheck className="w-5 h-5 text-green-700" /> Chantiers à réaliser</div>
            <div className="text-sm text-stone-600">{aPlanifier} à planifier · {chantiers.length - aPlanifier} planifiés</div>
          </div>
          <Button onClick={() => setOuvert(true)} className="bg-green-700 hover:bg-green-800 text-white" data-testid="car-ajouter-btn"><Plus className="w-4 h-4 mr-1" /> Ajouter un chantier</Button>
        </div>
        {chantiers.length === 0 && <div className="p-8 text-center text-stone-500" data-testid="car-vide">Aucun chantier en attente. Ajoutez-en ici ou dites à Valentine « ajoute un chantier… ».</div>}
        {chantiers.map(c => (
          <LigneChantier key={c.id} c={c} materiels={materiels} equipe={equipe} onPlanifier={proposer}
            onTerminer={(x) => action(x, 'terminer')} onSupprimer={(x) => action(x, 'supprimer')} />
        ))}
      </CardContent>

      <Dialog open={ouvert} onOpenChange={setOuvert}>
        <DialogContent className="max-w-2xl bg-white" data-testid="car-dialog">
          <DialogHeader><DialogTitle>Nouveau chantier à réaliser</DialogTitle></DialogHeader>
          <FormChantier form={form} setForm={setForm} materiels={materiels} />
          <Button onClick={creer} disabled={occupe} className="bg-green-700 hover:bg-green-800 text-white" data-testid="car-creer-btn">Ajouter</Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!cible} onOpenChange={(o) => { if (!o) { setCible(null); setProp(null); } }}>
        <DialogContent className="max-w-xl bg-white" data-testid="car-prop-dialog">
          <DialogHeader><DialogTitle>Créneau proposé — {cible?.prestation} chez {cible?.client_nom}</DialogTitle></DialogHeader>
          {!prop ? <div className="text-sm text-stone-500">Recherche des disponibilités…</div> : (
            <>
              <Proposition prop={prop} setProp={setProp} equipe={equipe} materiels={materiels} />
              <Button onClick={valider} disabled={occupe} className="bg-green-700 hover:bg-green-800 text-white" data-testid="car-valider-btn">Valider et ajouter au planning</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}
